import { ApiErrorLike } from "types/api";

export const formatMoney = (value?: number | string | null) => {
  const amount = Number(value ?? 0);

  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    maximumFractionDigits: 2,
  }).format(Number.isNaN(amount) ? 0 : amount);
};

export const formatDate = (value?: string | null) => {
  if (!value) {
    return "Not available";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Not available";
  }

  return new Intl.DateTimeFormat("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

export const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "Not available";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Not available";
  }

  return new Intl.DateTimeFormat("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const formatRelativeTime = (value?: string | null) => {
  if (!value) {
    return "Unknown";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }

  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const relativeTimeFormatter = new Intl.RelativeTimeFormat("en", {
    numeric: "auto",
  });

  const formatUnit = (amount: number, unit: Intl.RelativeTimeFormatUnit) =>
    relativeTimeFormatter.format(amount, unit);

  const absSeconds = Math.abs(diffSeconds);

  if (absSeconds < 60) {
    return formatUnit(diffSeconds, "second");
  }

  const diffMinutes = Math.round(diffSeconds / 60);

  if (Math.abs(diffMinutes) < 60) {
    return formatUnit(diffMinutes, "minute");
  }

  const diffHours = Math.round(diffSeconds / 3600);

  if (Math.abs(diffHours) < 24) {
    return formatUnit(diffHours, "hour");
  }

  const diffDays = Math.round(diffSeconds / 86400);

  if (Math.abs(diffDays) < 30) {
    return formatUnit(diffDays, "day");
  }

  const diffMonths = Math.round(diffDays / 30);

  if (Math.abs(diffMonths) < 12) {
    return formatUnit(diffMonths, "month");
  }

  return formatUnit(Math.round(diffDays / 365), "year");
};

export const formatCountdown = (seconds?: number | null) => {
  const totalSeconds = Number(seconds ?? 0);

  if (!Number.isFinite(totalSeconds)) {
    return "Start time pending";
  }

  if (totalSeconds <= 0) {
    return "Live or started";
  }

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (days > 0) {
    return `Starts in ${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `Starts in ${hours}h ${minutes}m`;
  }

  return `Starts in ${minutes}m`;
};

export const pluralize = (count: number, singular: string, plural?: string) =>
  `${count} ${count === 1 ? singular : plural ?? `${singular}s`}`;

export const getInitials = (value?: string | null) =>
  (value ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

export const safeIncludes = (value: unknown, query: string) =>
  String(value ?? "")
    .toLowerCase()
    .includes(query.trim().toLowerCase());

export const hasFileLikeValue = (value: unknown): boolean => {
  if (typeof File !== "undefined" && value instanceof File) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.some(hasFileLikeValue);
  }

  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).some(hasFileLikeValue);
  }

  return false;
};

export const getApiErrorMessage = (error: unknown, fallback: string) => {
  const apiError = error as ApiErrorLike;

  return (
    apiError?.response?.data?.error ||
    apiError?.response?.data?.message ||
    apiError?.response?.data?.name ||
    apiError?.message ||
    fallback
  );
};

export const isRecoverableUploadResponseError = (error: unknown, payload: unknown) =>
  hasFileLikeValue(payload) &&
  (error as ApiErrorLike)?.response?.status === 500 &&
  (error as ApiErrorLike)?.response?.data?.error === "Something went wrong.";
